import React, { useState } from "react";
import { toast } from "react-toastify";
import { useRules } from "../provider/RulesContext";
import {
  backArrowIcon,
  deleteIcon,
  editIcon,
  fileIcon,
  miniLoadingCricle,
  miniPlusIcon,
  miniSpinner,
  miniSpinnerGreen,
  playButtonIcon,
  smallTickIcon,
  uploadIcon,
  duplicate,
  SquareArrowOutUpRight,
} from "../../../assets/icons";
import { useNavigate } from "react-router-dom";
import useHandleFileUpload from "../../../hooks/useHandleFileUpload";
import { showConfirmDelete } from "../../../helpers";
import { useSelector } from "react-redux";
import RuleExecutionSummary from "../../../components/RuleExecutionSummary";
import { updatExecutionCount } from "../../../api/mappingServices";
import { DescriptionIcon } from "../../../assets/icons";
import { getOverLimitChecks } from "../../../store/userSlice";

const DescriptionModal = ({ isOpen, onClose, description, mappingName }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {mappingName} - Description
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const RulesData = () => {
  const { user } = useSelector((state) => {
    return state.user;
  });

  const overLimitChecks = useSelector(getOverLimitChecks);
  const {
    loading,
    mappings,
    deleteMapping,
    backToProjects,
    selectedProject,
    create_duplicate,
  } = useRules();

  const [selectedFile, setSelectedFile] = useState(null);
  const { handleFileUpload } = useHandleFileUpload();
  const navigate = useNavigate();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [summaryButton, setSummaryButton] = useState({ id: "" });
  const [summaryData, setSummaryData] = useState(null);
  // Description Modal State
  const [descriptionModal, setDescriptionModal] = useState({
    isOpen: false,
    description: "",
    mappingName: "",
  });

  // ✅ Extract access controls for current project
  const projectAccess =
    user?.role === "super_admin"
      ? {
          create: true,
          deleteOwn: true,
          deleteAny: true,
          execute: true,
        }
      : user?.projectsAssigned?.find((p) => p.project === selectedProject?._id)
          ?.accessControls || {
          create: false,
          deleteOwn: false,
          deleteAny: false,
          execute: false,
        };

  const canDeleteMapping = (mapping) => {
    if (user?.role === "super_admin") return true;
    const isCreatedByUser = mapping?.createdBy?._id === user?._id;
    return (
      projectAccess.deleteAny || (projectAccess.deleteOwn && isCreatedByUser)
    );
  };

  // Helper function to check if user can edit a specific mapping
  const canEditMapping = (mapping) => {
    if (user?.role === "super_admin") return true;
    const isCreatedByUser = mapping?.createdBy?._id === user?._id;
    return isCreatedByUser; // Only creator can edit
  };

  const deleteRuleConfirm = (ruleId, ruleName) => {
    showConfirmDelete(
      ruleName,
      () => {
        deleteMapping(ruleId);
      },
      () => {
        // Do nothing on cancel
      },
    );
  };

  const runRuleOnFile = async (rule) => {
    if (!selectedFile) {
      toast.dismiss();
      toast.error("Please upload a file before running the rule.");
      return;
    }
    if (!projectAccess.execute) {
      toast.error(
        "You don't have permission to execute rules for this project.",
      );
      return;
    }

    try {
      const response = await updatExecutionCount();

      if (!response.success) {
        toast.error(response.message);
        return;
      } else {
        setSummaryButton({
          id: rule?._id,
        });
        const finalSummary = await handleFileUpload(selectedFile, rule);
        setSummaryData(finalSummary.finalSummary);
      }
    } catch (error) {
      if (!error.success) {
        toast.error(
          error.message || "Execution limit exceeded. Please try again later.",
        );
      }
    }
  };

  const getControlsText = () => {
    return (
      <span>
        You have <span className="italic font-bold">permissions</span> to:{" "}
        {projectAccess.create && (
          <span className="text-green-600 font-medium">
            Create new mappings
          </span>
        )}
        {projectAccess.deleteAny && (
          <>
            ,{" "}
            <span className="text-red-600 font-medium">Delete Any mapping</span>
          </>
        )}
        {!projectAccess.deleteAny && projectAccess.deleteOwn && (
          <>
            ,{" "}
            <span className="text-red-600 font-medium">
              Delete Own mappings
            </span>
          </>
        )}
        {projectAccess.execute && (
          <>
            ,{" "}
            <span className="text-blue-600 font-medium">Execute mappings</span>.
          </>
        )}
        {!projectAccess.create &&
          !projectAccess.deleteAny &&
          !projectAccess.deleteOwn &&
          !projectAccess.execute && (
            <span className="text-gray-500">No Permissions.</span>
          )}
      </span>
    );
  };

  const openDescriptionModal = (description, mappingName) => {
    setDescriptionModal({
      isOpen: true,
      description,
      mappingName,
    });
  };

  const closeDescriptionModal = () => {
    setDescriptionModal({
      isOpen: false,
      description: "",
      mappingName: "",
    });
  };

  if (loading === "fetchingRules") {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full">
        <div className="inline-flex items-center px-4 py-2 bg-white rounded-lg shadow-md">
          {miniLoadingCricle}
          <span className="text-gray-700 font-medium">
            Loading Please wait...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-50 flex flex-col">
      {/* Header Section with Create Button */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex max-md:flex-col justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-500 text-white"
                onClick={backToProjects}
                aria-label="Go Back"
              >
                {backArrowIcon}
              </button>
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold text-gray-900">Mappings</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage and execute your data mapping rules
                </p>
              </div>
            </div>
            {/* Only show create button if user has create permission */}
            {projectAccess.create && (
              <button
                className={`inline-flex items-center px-6 py-3 max-md:my-2 bg-lightBlue text-white font-medium rounded-lg hover:bg-darkBlue focus:outline-none 
                focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm ${overLimitChecks.mappingsExceeded ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => window.open("/portal/create-rules", "_blank")}
                disabled={overLimitChecks.mappingsExceeded}
              >
                {miniPlusIcon}
                Create Mappings
              </button>
            )}
          </div>
        </div>
      </div>

      <RuleExecutionSummary
        isOpen={isSummaryOpen}
        setIsOpen={setIsSummaryOpen}
        onClose={() => setIsSummaryOpen(false)}
        summaryData={summaryData}
      />
      {/* Description Modal */}
      <DescriptionModal
        isOpen={descriptionModal.isOpen}
        onClose={closeDescriptionModal}
        description={descriptionModal.description}
        mappingName={descriptionModal.mappingName}
      />
      {/* Main Content */}
      <div className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-8 overflow-auto">
        {mappings?.length > 0 ? (
          <>
            <div className="mb-2 lg:mb-4">
              <h2 className="text-2xl font-bold text-blue-800 tracking-tight mb-2">
                {selectedProject?.projectName}
              </h2>
              <hr className="border-blue-200 mb-2" />
            </div>

            {/* File Upload Section - Only show if user can execute */}
            {projectAccess.execute && (
              <div className="mb-8">
                <div className="w-full border-2 border-dashed border-blue-400 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150 transition-all duration-300 shadow-sm">
                  <label
                    htmlFor="file-upload"
                    className="flex px-6 py-8 flex-col items-center justify-center space-y-3 text-center text-blue-700 cursor-pointer w-full h-full"
                  >
                    <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center mb-2">
                      {uploadIcon}
                    </div>
                    <span className="text-lg font-semibold">
                      Choose a file to execute mappings
                    </span>
                    <span className="text-sm text-gray-600">
                      CSV or Excel files only (Max 10MB)
                    </span>
                    {selectedFile && (
                      <div className="mt-4 px-4 py-2 bg-green-100 border border-green-300 rounded-lg">
                        <span className="text-sm text-green-700 font-medium flex items-center">
                          {smallTickIcon}
                          Selected: {selectedFile.name}
                        </span>
                      </div>
                    )}
                    <input
                      id="file-upload"
                      type="file"
                      accept=".csv, .xlsx, .xls"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="hidden"
                      disabled={overLimitChecks.executionsExceeded}
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Rules List */}
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  Available Mappings ({mappings.length})
                </h2>
                <p className="text-sm text-gray-600">{getControlsText()}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {mappings.map((rule, index) => {
                  const canEdit = canEditMapping(rule);
                  const canDelete = canDeleteMapping(rule);
                  const hasDescription =
                    rule.description && rule.description.trim() !== "";

                  return (
                    <div
                      key={index}
                      className="bg-white shadow-md rounded-xl p-6 flex flex-col justify-between border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="mb-4">
                        <div className="flex justify-between">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2 break-words line-clamp-2">
                            {rule.mappingName}
                          </h3>
                          {summaryButton.id === rule._id && (
                            <button
                              className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                              title="View Summary"
                              onClick={() => {
                                setIsSummaryOpen(true);
                              }}
                            >
                              {SquareArrowOutUpRight}
                            </button>
                          )}
                          {hasDescription && (
                            <button
                              onClick={() =>
                                openDescriptionModal(
                                  rule.description,
                                  rule.mappingName,
                                )
                              }
                              className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1"
                              title="View description"
                            >
                              <DescriptionIcon />
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full inline-block">
                          Rule #{index + 1}
                        </div>
                      </div>
                      <div className="mb-4 space-y-2">
                        <div>
                          <p className="text-gray-600 font-medium">
                            Created By:
                          </p>
                          <p className="text-gray-800">
                            {rule?.createdBy?.firstName}{" "}
                            {rule?.createdBy?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {rule?.createdBy?.email}
                          </p>
                        </div>

                        {/* Last Modified By */}
                        {rule?.updatedBy && (
                          <div>
                            <p className="text-gray-600 font-medium">
                              Last Modified By:
                            </p>
                            <p className="text-gray-800">
                              {rule?.updatedBy?.firstName}{" "}
                              {rule?.updatedBy?.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {rule?.updatedBy?.email}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                        {/* Execute button - only if user has execute permission */}
                        {projectAccess.execute && (
                          <button
                            className={`flex items-center justify-center w-10 h-10 text-blue-600 hover:text-white hover:bg-blue-600 border-2
                               border-blue-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 
                                ${overLimitChecks.executionsExceeded ? "opacity-50 cursor-not-allowed" : ""}
                               `}
                            title="Run Rule"
                            onClick={() => runRuleOnFile(rule)}
                            disabled={overLimitChecks.executionsExceeded}
                          >
                            {playButtonIcon}
                          </button>
                        )}

                        {/* Action buttons container */}
                        <div className="flex space-x-2">
                          {/* Edit button - only for rule creator */}
                          {canEdit && (
                            <button
                              className={`flex items-center justify-center w-10 h-10 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200
                                ${overLimitChecks.mappingsExceeded ? "opacity-50 cursor-not-allowed" : ""}
                               `}
                              title="Edit Rule"
                              onClick={() => {
                                navigate("/portal/create-rules", {
                                  state: { rule },
                                });
                              }}
                              disabled={overLimitChecks.mappingsExceeded}
                            >
                              {editIcon}
                            </button>
                          )}

                          {/* Duplicate button - only if user can create */}
                          {projectAccess.create && (
                            <button
                              className={`flex items-center justify-center w-10 h-10 text-gray-600 hover:text-lightGreen hover:bg-green-50 rounded-full focus:outline-none focus:ring-2 focus:ring-lightGreen focus:ring-offset-2 transition-all duration-200
                                ${overLimitChecks.mappingsExceeded ? "opacity-50 cursor-not-allowed" : ""}
                               `}
                              title="Duplicate Rule"
                              onClick={() => {
                                create_duplicate(rule);
                              }}
                              disabled={overLimitChecks.mappingsExceeded}
                            >
                              {loading === `creatingDuplicate_${rule._id}`
                                ? miniSpinnerGreen
                                : duplicate}
                            </button>
                          )}

                          {/* Delete button - based on deleteOwn/deleteAny permissions */}
                          {canDelete && (
                            <button
                              className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200"
                              title={`Delete Rule ${
                                projectAccess.deleteAny ? "(Admin)" : "(Own)"
                              }`}
                              onClick={() => {
                                deleteRuleConfirm(rule._id, rule.mappingName);
                              }}
                            >
                              {loading === `deletingRule_${rule._id}`
                                ? miniSpinner
                                : deleteIcon}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              {fileIcon}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No rules found
            </h3>
            <p className="text-gray-500 mb-6">
              {projectAccess.create
                ? "Get started by creating your first mapping rule"
                : "No mapping rules are available for this project"}
            </p>
            {projectAccess.create && (
              <button
                className="inline-flex items-center px-6 py-3 bg-lightBlue text-white font-medium rounded-lg hover:bg-darkBlue focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                onClick={() => window.open("/portal/create-rules", "_blank")}
              >
                {miniPlusIcon}
                Create Your First Rule
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesData;
